import React, {ReactNode, useEffect, useMemo, useState} from 'react';
import {ProfilesClient} from '@customink/profiles-sso-spa-client';
import {AuthContext, AuthContextData, AuthUser} from '../AuthContext';
import PropTypes from 'prop-types';
import RemoteServices from '~/constants/RemoteServices';

const propTypes = {
  children: PropTypes.node
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export const ProfilesProvider: React.FC<{children: ReactNode}> = ({
  children
}) => {
  const profilesClient = useMemo(
    () =>
      new ProfilesClient({
        issuer: RemoteServices.profilesUrl,
        clientId: RemoteServices.profilesClientId,
        redirectUri: RemoteServices.profilesRedirectUri,
        useCache: false
      }),
    []
  );

  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [issuedAt, setIssuedAt] = useState<number | undefined>(undefined);

  const providerValue = useMemo<AuthContextData>(
    () => ({
      logout: async () => profilesClient.logout(),
      loginWithPopup: async () => {
        await profilesClient.loginWithPopup();
      },
      loginWithRedirect: async () => {
        return profilesClient.loginWithRedirect();
      },
      loginSilently: async () => {
        await profilesClient.loginSilently();
      },
      getTokens: async () => {
        const {id_token: idToken, access_token: accessToken} =
          await profilesClient.getTokensSilently();
        return {idToken, accessToken};
      },
      identityProvider: 'profiles',
      user
    }),
    [profilesClient, user, issuedAt]
  );

  useEffect(() => {
    const initializeClient = async () => {
      profilesClient.subscribe(({isAuthenticated, userInfo, iat}) => {
        if (!isAuthenticated || !userInfo || !iat) {
          return;
        }

        setIssuedAt(iat);
        setUser({
          id: userInfo.sub,
          isInternal: Boolean(userInfo.internal),
          email: userInfo.email,
          fullName: userInfo.name.trim().split('@')[0] // the split on '@' patches a profiles bug where sometimes this field wrongly contains email instead of null.
        });
      });

      await profilesClient.start();
    };

    initializeClient();
  }, [profilesClient]);

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};

ProfilesProvider.propTypes = propTypes;
